import React, { useState, useEffect, useMemo, useCallback, useRef, memo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Building2, MapPin, FileText, Upload, Download, Plus, Trash2, Zap, Eye, CheckCircle2, FileSpreadsheet, AlertCircle, RefreshCcw, Pencil, Clock } from 'lucide-react';
import * as XLSX from 'xlsx';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ReferenceLine, Brush } from 'recharts';
import parserDonneesBrutesTURPE, { extractPDLFromCSV, extractPDLFromFilename, parserENEDISMultiPDL, detecterPDLsMultiples } from '../utils/parserDonneesBrutesTURPE';
import { parseEnedisFileStreaming } from '../utils/parseEnedisWorker';
import ReactifDiagnosticBlock from '../components/ReactifDiagnosticBlock';
import { analyserDonneesBrutes, preconiserPuissances } from '../utils/analyseTURPE';
import { ErrorDebugModal } from '../components/ErrorDebugModal';
import { TENSIONS, FORMULES_PAR_TENSION, CONTRATS, PLAGES_PAR_FORMULE, TARIFS_PAR_TENSION, COEFFICIENTS_PAR_FORMULE, AGENCES_DATA, COMMERCIAUX_DATA } from './turpe/constants';
import { StepTypeSelection } from './turpe/StepTypeSelection';
import { StepClientInfo } from './turpe/StepClientInfo';
import { StepPDLConfig } from './turpe/StepPDLConfig';
import { PlagesHorairesConfig } from './turpe/PlagesHorairesConfig';
import { Step4Donnees } from '../components/turpe/Step4Donnees';
import { Step5Analyse } from '../components/turpe/Step5Analyse';
import { Step6Validation } from '../components/turpe/Step6Validation';
import { Step7Generation } from '../components/turpe/Step7Generation';
import {
  getBrouillon as apiGetBrouillon,
  saveBrouillon as apiSaveBrouillon,
  migrateLocalStorageBrouillons,
} from '../utils/brouillonsApi';

// Échantillonne une courbe (max N points) tout en conservant sa forme.
// Utilisé pour stocker une version LÉGÈRE des courbes de charge dans le brouillon
// (les données brutes tousLesPoints sont trop volumineuses pour MongoDB à ~200 PDL).
const capGraphBrouillon = (arr, max = 500) => {
  if (!Array.isArray(arr) || arr.length <= max) return arr;
  const step = arr.length / max;
  const out = [];
  for (let i = 0; i < max; i++) out.push(arr[Math.floor(i * step)]);
  const dernier = arr[arr.length - 1];
  if (out[out.length - 1] !== dernier) out.push(dernier);
  return out;
};

// Composant de commentaire optimisé - évite les re-renders du parent à chaque frappe
const CommentaireInput = memo(({ pdl, initialValue, onSave }) => {
  const textareaRef = useRef(null);
  
  // Mettre à jour la valeur initiale quand elle change (ex: chargement brouillon)
  useEffect(() => {
    if (textareaRef.current && initialValue !== undefined) {
      textareaRef.current.value = initialValue;
    }
  }, [initialValue]);
  
  return (
    <div className="mt-6 bg-amber-50 border border-amber-200 rounded-lg p-4">
      <div className="flex items-start gap-3">
        <span className="text-amber-500 text-xl">📝</span>
        <div className="flex-1">
          <label className="block text-sm font-semibold text-amber-800 mb-2">
            Remarques / Commentaires pour ce PDL
          </label>
          <textarea
            ref={textareaRef}
            defaultValue={initialValue || ''}
            onBlur={(e) => onSave(pdl, e.target.value)}
            placeholder="Ajoutez ici vos remarques, observations ou points de clarification pour ce PDL..."
            className="w-full px-3 py-2 border border-amber-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-400 focus:border-amber-400 min-h-[80px] resize-y"
            rows={3}
          />
          <p className="text-xs text-amber-600 mt-1">
            Ce commentaire sera sauvegardé quand vous quitterez le champ.
          </p>
        </div>
      </div>
    </div>
  );
});
CommentaireInput.displayName = 'CommentaireInput';

// Composant d'input de puissance optimisé - évite les re-renders du parent à chaque frappe
const PuissanceInput = memo(({ pdl, plage, initialValue, onSave, className, placeholder = "kVA" }) => {
  const inputRef = useRef(null);
  
  useEffect(() => {
    if (inputRef.current && initialValue !== undefined) {
      inputRef.current.value = initialValue || '';
    }
  }, [initialValue]);
  
  return (
    <input
      ref={inputRef}
      type="number"
      defaultValue={initialValue || ''}
      onBlur={(e) => {
        const value = parseFloat(e.target.value) || 0;
        onSave(pdl, plage, value);
      }}
      placeholder={placeholder}
      className={className}
      min="0"
      step="10"
    />
  );
});
PuissanceInput.displayName = 'PuissanceInput';

// Hook personnalisé pour debounce des valeurs (non utilisé pour commentaires mais gardé pour autres usages)
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);
  
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);
    
    return () => clearTimeout(handler);
  }, [value, delay]);
  
  return debouncedValue;
};

const CreateAnalyseTURPE = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState(1);
  const [analyseType, setAnalyseType] = useState(''); // 'mono' ou 'multi'
  
  const [formData, setFormData] = useState({
    // Client
    nom_client: '',
    siren: '', // SIREN de l'entreprise
    
    // PDL
    pdls: [], // [{pdl, tension, formule_actuelle, contrat, puissances_actuelles: {}}]
    
    // Commercial
    agence_id: '',
    commercial_id: ''
  });

  const [currentPDL, setCurrentPDL] = useState({
    pdl: '',
    tension: '',
    formule_actuelle: '',
    contrat: '',
    puissances_actuelles: {},
    commentaire: '' // Remarque/commentaire pour ce PDL
  });

  const [agences, setAgences] = useState([]);
  const [commerciaux, setCommerciaux] = useState([]);
  
  // Brouillons
  const [brouillonId, setBrouillonId] = useState(null);
  
  // Données brutes et résultats
  const [puissancesMaxParPDL, setPuissancesMaxParPDL] = useState({});
  const [analyseResults, setAnalyseResults] = useState(null);
  const [preconisations, setPreconisations] = useState(null);
  const [zoomReset, setZoomReset] = useState(0);
  
  // Debug erreurs
  const [errorDebug, setErrorDebug] = useState(null);
  const [errorContext, setErrorContext] = useState(null);
  
  // Visibilité courbes graphiques
  const [visibiliteCourbes, setVisibiliteCourbes] = useState({
    kW: true,
    kVA: true,
    puissanceSouscrite: true,
    puissancePreconisee: true
  });
  
  // Préconisations de puissance par PDL (valeur unique pour affichage courbe) - useRef pour éviter re-renders
  const preconisationsPuissanceRef = useRef({});
  
  // Préconisations de puissance par plage par PDL (pour calculs détaillés) - useRef pour éviter re-renders
  const preconisationsPuissanceParPlageRef = useRef({});
  
  // Consommations par plage par PDL (modifiables) - useRef pour éviter re-renders
  const consommationsParPlageRef = useRef({});
  
  // Commentaires par PDL (remarques de l'energy manager)
  const [commentairesParPDL, setCommentairesParPDL] = useState({});
  
  // Puissances max factures par PDL (saisie manuelle mensuelle)
  const [puissancesFacturesParPDL, setPuissancesFacturesParPDL] = useState({});
  
  // Override CER (énergie réactive) en € par PDL : { pdl: { actuel: €, propose: € } }
  const cerOverrideRef = useRef({});
  // Saisie manuelle CMDPS (dépassements) en € par PDL : { pdl: { actuel?: €, propose?: € } }
  const cmdpsOverrideRef = useRef({});

  // Plages horaires personnalisées par PDL
  const plagesHorairesCustomRef = useRef({});
  const [plagesHorairesModalPDL, setPlagesHorairesModalPDL] = useState(null);

  
  // Callback memoized pour sauvegarder les commentaires (évite les re-renders)
  const handleSaveCommentaire = useCallback((pdl, value) => {
    setCommentairesParPDL(prev => ({
      ...prev,
      [pdl]: value
    }));
  }, []);
  
  // Callback memoized pour sauvegarder les puissances par plage (useRef - pas de re-render)
  const handleSavePuissanceParPlage = useCallback((pdl, plage, value) => {
    preconisationsPuissanceParPlageRef.current = {
      ...preconisationsPuissanceParPlageRef.current,
      [pdl]: {
        ...(preconisationsPuissanceParPlageRef.current[pdl] || {}),
        [plage]: value
      }
    };
    // Pour les formules mono-plage (Unique, Base), synchroniser avec la puissance courbe
    if (plage === 'Unique' || plage === 'Base') {
      preconisationsPuissanceRef.current = {
        ...preconisationsPuissanceRef.current,
        [pdl]: value
      };
    }
    setCalculsAJour(false);
  }, []);
  
  // Callback memoized pour sauvegarder la puissance courbe (useRef - pas de re-render)
  const handleSavePuissanceCourbe = useCallback((pdl, _, value) => {
    preconisationsPuissanceRef.current = {
      ...preconisationsPuissanceRef.current,
      [pdl]: value
    };
    setCalculsAJour(false);
  }, []);
  
  // Callback memoized pour sauvegarder les consommations par plage (useRef - pas de re-render)
  const handleSaveConsommationParPlage = useCallback((pdl, plage, value) => {
    consommationsParPlageRef.current = {
      ...consommationsParPlageRef.current,
      [pdl]: {
        ...(consommationsParPlageRef.current[pdl] || {}),
        [plage]: value
      }
    };
    setCalculsAJour(false);
  }, []);

  // Callback pour sauvegarder les puissances factures par mois par PDL
  const handleSavePuissanceFacture = useCallback((pdlKey, moisKey, value) => {
    setPuissancesFacturesParPDL(prev => ({
      ...prev,
      [pdlKey]: {
        ...(prev[pdlKey] || {}),
        [moisKey]: parseFloat(value) || 0
      }
    }));
  }, []);
  
  // Calculs TURPE pour le rapport (stockés après l'étape 6)
  const [calculsRapport, setCalculsRapport] = useState(null);
  
  // Calculs TURPE par PDL pour le multi-site (stockés après l'étape 5)
  const [calculsParPDL, setCalculsParPDL] = useState({});
  
  // Métadonnées des données importées par PDL (hasReactiveData, pasTempsMinutes)
  const [metadonneesPDL, setMetadonneesPDL] = useState({});
  
  // Récapitulatif visuel du dernier import groupé (multi-PDL)
  const [importRecap, setImportRecap] = useState(null);
  
  // Flag pour savoir si les calculs doivent être mis à jour
  const [calculsAJour, setCalculsAJour] = useState(true);
  
  // Puissances préconisées validées (affichées sur la courbe uniquement après clic "Actualiser")
  const [puissancesPreconiseesValidees, setPuissancesPreconiseesValidees] = useState({});
  
  // Fonction d'actualisation des calculs
  const actualiserCalculs = () => {
    // Copier les valeurs saisies (refs) vers les valeurs validées (state) pour affichage sur courbe
    setPuissancesPreconiseesValidees({...preconisationsPuissanceRef.current});
    setCalculsAJour(true);
    setCalculsParPDL({});
  };
  
  // Vérifie si une heure tombe dans une liste de créneaux
  const heureEstDansCreneaux = (heure, creneaux) => {
    if (!creneaux || creneaux.length === 0) return false;
    return creneaux.some(c => {
      if (c.debut < c.fin) return heure >= c.debut && heure < c.fin;
      return heure >= c.debut || heure < c.fin; // Créneau passant minuit
    });
  };

  // Fonction pour déterminer la plage horaire TURPE (supporte plages personnalisées)
  const determinerPlageHoraire = (horodate, formule, pdl) => {
    // Formules mono-plage (Unique/Base) : toujours retourner "Unique"
    const plagesFormule = PLAGES_PAR_FORMULE[formule];
    if (plagesFormule && plagesFormule.length === 1 && plagesFormule[0] === 'Unique') {
      return 'Unique';
    }
    
    // Formules HP/HC (double tarif) : retourner HP ou HC selon l'heure
    if (plagesFormule && plagesFormule.length === 2 && plagesFormule.includes('HP') && plagesFormule.includes('HC')) {
      const date = new Date(horodate);
      const heure = date.getHours();
      const jour = date.getDay();
      const isWeekend = (jour === 0 || jour === 6);
      
      // Check custom plages
      const customPlages = pdl ? plagesHorairesCustomRef.current[pdl] : null;
      if (customPlages && Object.keys(customPlages).length > 0) {
        const mois = date.getMonth() + 1;
        const isHiver = (customPlages.moisHiver || [11, 12, 1, 2, 3]).includes(mois);
        if (isWeekend && customPlages.weekendToutHC) return 'HC';
        const periodeConfig = isHiver
          ? (isWeekend ? customPlages.hiverWeekend : customPlages.hiverSemaine)
          : (isWeekend ? customPlages.eteWeekend : customPlages.eteSemaine);
        if (periodeConfig && heureEstDansCreneaux(heure, periodeConfig.hp)) return 'HP';
        return 'HC';
      }
      
      // Heures creuses par défaut : 22h-6h et week-end
      const isHC = (heure >= 22 || heure < 6) || isWeekend;
      return isHC ? 'HC' : 'HP';
    }

    const date = new Date(horodate);
    const mois = date.getMonth() + 1;
    const heure = date.getHours();
    const jour = date.getDay();
    const isWeekend = (jour === 0 || jour === 6);

    // Vérifier si des plages personnalisées existent pour ce PDL
    const customPlages = pdl ? plagesHorairesCustomRef.current[pdl] : null;
    
    if (customPlages && Object.keys(customPlages).length > 0) {
      const moisHiver = customPlages.moisHiver || [11, 12, 1, 2, 3];
      const isHiver = moisHiver.includes(mois);
      
      // Weekend tout HC
      if (isWeekend && customPlages.weekendToutHC) {
        return isHiver ? 'HCH' : 'HCB';
      }
      
      // Déterminer la période
      let periodeConfig;
      if (isHiver) {
        periodeConfig = isWeekend ? customPlages.hiverWeekend : customPlages.hiverSemaine;
      } else {
        periodeConfig = isWeekend ? customPlages.eteWeekend : customPlages.eteSemaine;
      }
      
      if (periodeConfig) {
        // Vérifier Pointe d'abord
        if (periodeConfig.pointe && heureEstDansCreneaux(heure, periodeConfig.pointe)) {
          return 'Pointe';
        }
        // Vérifier HP
        if (heureEstDansCreneaux(heure, periodeConfig.hp)) {
          return isHiver ? 'HPH' : 'HPB';
        }
        // Sinon HC
        return isHiver ? 'HCH' : 'HCB';
      }
    }
    
    // Plages prédéfinies (logique par défaut)
    const isHiver = (mois >= 11 || mois <= 3);
    
    if (formule && (formule.includes('CU5') || formule.includes('LU5'))) {
      const isPointe = (mois >= 12 || mois <= 2) && !isWeekend && 
                       ((heure >= 9 && heure < 11) || (heure >= 18 && heure < 20));
      if (isPointe) return 'Pointe';
    }
    
    const isHeuresCreuses = (heure >= 22 || heure < 6) || isWeekend;
    
    if (isHiver) {
      return isHeuresCreuses ? 'HCH' : 'HPH';
    } else {
      return isHeuresCreuses ? 'HCB' : 'HPB';
    }
  };
  
  // Fonction pour calculer les consommations par plage à partir des données brutes
  const calculerConsommationsParPlage = (tousLesPoints, formule, pdlNum) => {
    const consommations = {
      Pointe: 0,
      HPH: 0,
      HCH: 0,
      HPB: 0,
      HCB: 0
    };
    
    // Détecter le pas de temps si disponible dans les métadonnées, sinon calculer
    let pasTempsMinutes = 10; // Défaut
    if (tousLesPoints.length >= 2) {
      // Trier et calculer la différence
      const pointsTries = [...tousLesPoints].sort((a, b) => 
        new Date(a.horodate) - new Date(b.horodate)
      );
      const diff = (new Date(pointsTries[1].horodate) - new Date(pointsTries[0].horodate)) / (1000 * 60);
      if (diff > 0 && diff <= 60) {
        // Arrondir au pas standard le plus proche (5, 10, 15, 30, 60)
        const pasStandards = [5, 10, 15, 30, 60];
        pasTempsMinutes = pasStandards.reduce((prev, curr) => 
          Math.abs(curr - diff) < Math.abs(prev - diff) ? curr : prev
        );
      }
    }
    
    // Facteur de conversion: kW × (pas en heures) = kWh
    const facteurConversion = pasTempsMinutes / 60;
    
    tousLesPoints.forEach(point => {
      const plage = determinerPlageHoraire(point.horodate, formule, pdlNum);
      // Convertir la puissance moyenne en énergie (kWh)
      // Energie = Puissance × Temps
      const energie = (point.kW || 0) * facteurConversion;
      consommations[plage] = (consommations[plage] || 0) + energie;
    });
    
    return consommations;
  };

  // Mode édition
  const [editMode, setEditMode] = useState(false);
  const [editRapportId, setEditRapportId] = useState(null);
  const [loadingEdit, setLoadingEdit] = useState(false);

  useEffect(() => {
    // Chargement des agences/commerciaux depuis l'API (avec fallback sur les constantes locales)
    const token = localStorage.getItem('token');
    Promise.all([
      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/agences`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.ok ? r.json() : null),
      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/commerciaux`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.ok ? r.json() : null),
    ]).then(([a, c]) => {
      setAgences(Array.isArray(a) && a.length ? a : AGENCES_DATA);
      setCommerciaux(Array.isArray(c) && c.length ? c : COMMERCIAUX_DATA);
    }).catch(() => {
      setAgences(AGENCES_DATA);
      setCommerciaux(COMMERCIAUX_DATA);
    });
    
    // Mode édition : charger rapport existant
    const editId = searchParams.get('edit');
    if (editId) {
      chargerRapportTURPEPourEdition(editId);
      return;
    }

    // Migration silencieuse des brouillons localStorage → serveur (1 fois)
    migrateLocalStorageBrouillons('turpe', (b) => ({
      nom_entreprise: b.formData?.nom_entreprise || '',
      siren: b.formData?.siren || '',
      step: b.step || 1,
      payload: {
        step: b.step,
        analyseType: b.analyseType,
        formData: b.formData,
        puissancesMaxParPDL: b.puissancesMaxParPDL,
        puissancesFacturesParPDL: b.puissancesFacturesParPDL,
        plagesHorairesCustom: b.plagesHorairesCustom,
      },
    })).catch(() => {});

    // Charger brouillon si URL
    const urlParams = new URLSearchParams(window.location.search);
    const brouillonParam = urlParams.get('brouillon');
    if (brouillonParam) {
      chargerBrouillon(brouillonParam);
    }
    
    // Pré-remplir si SIREN et nom passés en paramètres (depuis Fiche Client)
    const sirenParam = searchParams.get('siren');
    const nomParam = searchParams.get('nom');
    if (sirenParam || nomParam) {
      setFormData(prev => ({
        ...prev,
        nom_client: nomParam || '',
        siren: sirenParam || ''
      }));
    }
  }, [searchParams]);

  // === Helper : reconstruire les données d'un PDL à partir de son graphData
  // échantillonné (utilisé au chargement d'un rapport OU d'un brouillon, mono & multi).
  const reconstruirePDLDepuisGraph = (pdlKey, graphData) => {
    const tousLesPoints = (graphData || []).map(p => ({
      horodate: p.horodate || p.date,
      date: p.horodate || p.date,
      kW: p.kW || 0,
      kVA: p.kVA || 0,
    }));
    let maxKW = 0, maxKVA = 0;
    tousLesPoints.forEach(p => {
      if (p.kW > maxKW) maxKW = p.kW;
      if (p.kVA > maxKVA) maxKVA = p.kVA;
    });
    const moisMap = {};
    tousLesPoints.forEach(p => {
      if (!p.horodate) return;
      const mois = p.horodate.substring(0, 7);
      if (!moisMap[mois]) moisMap[mois] = { pmax_kW: 0, pmax_kVA: 0, nb_points: 0 };
      moisMap[mois].nb_points++;
      if (p.kW > moisMap[mois].pmax_kW) moisMap[mois].pmax_kW = p.kW;
      if (p.kVA > moisMap[mois].pmax_kVA) moisMap[mois].pmax_kVA = p.kVA;
    });
    const puissancesMaxParMois = Object.entries(moisMap)
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([mois, data]) => {
        const [annee, moisNum] = mois.split('-').map(Number);
        const joursParMois = new Date(annee, moisNum, 0).getDate();
        const attendu = joursParMois * 288;
        const taux = data.nb_points / attendu * 100;
        return {
          mois,
          pmax_kW: data.pmax_kW,
          pmax_kVA: data.pmax_kVA,
          nb_points: data.nb_points,
          taux_complet: taux,
          qualite: taux >= 95 ? 'Bon' : taux >= 80 ? 'Moyen' : 'Faible',
        };
      });
    return {
      pdlBlock: { tousLesPoints, puissancesMaxParMois, pmax_kW: maxKW, pmax_kVA: maxKVA, donneesDegradees: true },
      meta: { hasReactiveData: maxKVA > 0, pasTempsMinutes: 10 },
    };
  };

  const chargerRapportTURPEPourEdition = async (rapportId) => {
    setLoadingEdit(true);
    try {
      const token = localStorage.getItem('token');
      // Charge en parallèle le rapport ET la liste des commerciaux/agences,
      // pour permettre la résolution de l'agence_id à partir du commercial_id
      // sur les anciens rapports qui ne l'avaient pas persistée.
      const [responseRapport, commsResp] = await Promise.all([
        fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/turpe/rapports/${rapportId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        ),
        fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/commerciaux`,
          { headers: { Authorization: `Bearer ${token}` } }
        ).then(r => r.ok ? r.json() : null).catch(() => null),
      ]);
      if (!responseRapport.ok) throw new Error('Rapport non trouvé');
      const rapport = await responseRapport.json();
      const commsList = Array.isArray(commsResp) && commsResp.length ? commsResp : COMMERCIAUX_DATA;
      
      setEditMode(true);
      setEditRapportId(rapportId);
      
      // Déterminer le type d'analyse
      const isMulti = rapport.type === 'multi' || (rapport.pdls && rapport.pdls.length > 1);
      setAnalyseType(isMulti ? 'multi' : 'mono');
      
      // Reconstruire formData
      const pdlConfig = rapport.pdl || {};
      const client = rapport.client || {};
      
      const pdls = isMulti && rapport.pdls 
        ? rapport.pdls.map(p => ({
            pdl: p.numero || (typeof p.pdl === 'object' ? p.pdl?.numero : p.pdl) || '',
            tension: p.tension || p.tensionActuelle || rapport.tensionActuelle || '',
            formule_actuelle: p.formuleActuelle || rapport.formuleActuelle || '',
            contrat: p.contrat || '',
            puissances_actuelles: (() => {
              const pa = p.puissancesActuelles || p.calculs?.puissancesActuelles;
              if (pa && Object.keys(pa).length > 0) return { ...pa };
              const ps = p.puissanceSouscrite || p.pdl?.puissanceSouscrite;
              return ps ? { Unique: ps } : {};
            })(),
            commentaire: p.commentaire || ''
          }))
        : [{
            pdl: pdlConfig.numero || '',
            tension: rapport.tensionActuelle || '',
            formule_actuelle: rapport.formuleActuelle || '',
            contrat: pdlConfig.contrat || '',
            puissances_actuelles: (rapport.calculs?.puissancesActuelles && Object.keys(rapport.calculs.puissancesActuelles).length > 0)
              ? { ...rapport.calculs.puissancesActuelles }
              : (pdlConfig.puissanceSouscrite ? { unique: pdlConfig.puissanceSouscrite } : {}),
            commentaire: ''
          }];
      
      setFormData({
        nom_client: client.nom || rapport.nom_entreprise || '',
        siren: client.siren || rapport.siren || '',
        pdls,
        agence_id: rapport.agence_id || (() => {
          // Fallback ancien rapport : déduire l'agence depuis le commercial_id sauvegardé.
          const cid = rapport.commercial_id;
          if (!cid) return '';
          const comm = commsList.find(c => String(c.id) === String(cid));
          return comm?.agence_id || comm?.agenceId || '';
        })(),
        commercial_id: rapport.commercial_id || ''
      });
      
      if (isMulti && Array.isArray(rapport.pdls) && rapport.pdls.length > 0) {
        // === MULTI-PDL : restaurer graphData + commentaires + plages + préconisations par PDL ===
        const puissAcc = {};
        const metaAcc = {};
        const preconAcc = {};
        const preconPlageAcc = {};
        const commAcc = {};
        const plagesAcc = {};
        rapport.pdls.forEach((p) => {
          const pdlKey = p.numero || (typeof p.pdl === 'object' ? p.pdl?.numero : p.pdl) || '';
          if (!pdlKey) return;
          if (p.graphData && p.graphData.length > 0) {
            const { pdlBlock, meta } = reconstruirePDLDepuisGraph(pdlKey, p.graphData);
            // Amélioration durable : conserver les agrégats de dépassement calculés sur les
            // données natives (pas 5/10 min) à la création, pour ne PAS recalculer la CMDPS
            // HTA à partir du graphData horaire dégradé si la PS n'a pas changé.
            pdlBlock.depassementsStockes = p.calculs?.depassements || null;
            pdlBlock.puissancesActuellesStockees = p.calculs?.puissancesActuelles || null;
            pdlBlock.puissancesProposeesStockees = p.calculs?.puissancesProposees || null;
            puissAcc[pdlKey] = pdlBlock;
            metaAcc[pdlKey] = meta;
          }
          if (p.calculs?.puissancePreconisee != null) {
            preconAcc[pdlKey] = p.calculs.puissancePreconisee;
          }
          if (p.calculs?.puissancesProposees) {
            preconPlageAcc[pdlKey] = p.calculs.puissancesProposees;
          }
          if (p.commentaire) commAcc[pdlKey] = p.commentaire;
          if (p.calculs?.cmdpsOverride) cmdpsOverrideRef.current[pdlKey] = p.calculs.cmdpsOverride;
          if (p.plagesHorairesCustom) plagesAcc[pdlKey] = p.plagesHorairesCustom;
        });
        if (Object.keys(puissAcc).length > 0) {
          setPuissancesMaxParPDL(puissAcc);
          setMetadonneesPDL(prev => ({ ...prev, ...metaAcc }));
        }
        if (Object.keys(preconAcc).length > 0) {
          preconisationsPuissanceRef.current = { ...preconisationsPuissanceRef.current, ...preconAcc };
          setPuissancesPreconiseesValidees(prev => ({ ...prev, ...preconAcc }));
        }
        if (Object.keys(preconPlageAcc).length > 0) {
          preconisationsPuissanceParPlageRef.current = { ...preconisationsPuissanceParPlageRef.current, ...preconPlageAcc };
        }
        if (Object.keys(commAcc).length > 0) {
          setCommentairesParPDL(prev => ({ ...prev, ...commAcc }));
        }
        if (Object.keys(plagesAcc).length > 0) {
          plagesHorairesCustomRef.current = { ...plagesHorairesCustomRef.current, ...plagesAcc };
        }
      } else if (rapport.graphData && rapport.graphData.length > 0) {
        // === MONO-PDL ===
        const pdlKey = pdlConfig.numero || pdls[0]?.pdl || 'PDL';
        const { pdlBlock, meta } = reconstruirePDLDepuisGraph(pdlKey, rapport.graphData);
        // Amélioration durable : conserver les agrégats natifs de dépassement (voir multi-PDL).
        pdlBlock.depassementsStockes = rapport.calculs?.depassements || null;
        pdlBlock.puissancesActuellesStockees = rapport.calculs?.puissancesActuelles || null;
        pdlBlock.puissancesProposeesStockees = rapport.calculs?.puissancesProposees || null;
        setPuissancesMaxParPDL({ [pdlKey]: pdlBlock });
        setMetadonneesPDL(prev => ({ ...prev, [pdlKey]: meta }));
      }

      // === Pré-remplir préconisations / commentaires / plages MONO (rétro-compat) ===
      if (!isMulti) {
        const pdlKey = pdlConfig.numero || pdls[0]?.pdl || 'PDL';
        if (rapport.calculs?.puissancePreconisee) {
          preconisationsPuissanceRef.current = { [pdlKey]: rapport.calculs.puissancePreconisee };
          setPuissancesPreconiseesValidees({ [pdlKey]: rapport.calculs.puissancePreconisee });
        }
        if (rapport.calculs?.puissancesProposees) {
          preconisationsPuissanceParPlageRef.current = { [pdlKey]: rapport.calculs.puissancesProposees };
        }
        if (rapport.commentaire) {
          setCommentairesParPDL(prev => ({ ...prev, [pdlKey]: rapport.commentaire }));
        }
        if (rapport.calculs?.cmdpsOverride) {
          cmdpsOverrideRef.current = { [pdlKey]: rapport.calculs.cmdpsOverride };
        }
        if (rapport.plagesHorairesCustom) {
          plagesHorairesCustomRef.current = { [pdlKey]: rapport.plagesHorairesCustom };
        }
      }

      // Sauter directement à l'étape 5 (analyse) - calculs marqués à jour pour accès step 6
      setStep(5);
      setCalculsAJour(true);
      
    } catch (error) {
      console.error('Erreur chargement rapport TURPE:', error);
      alert('Erreur lors du chargement du rapport TURPE pour modification.');
    } finally {
      setLoadingEdit(false);
    }
  };

  const chargerBrouillon = async (id) => {
    try {
      const brouillon = await apiGetBrouillon(id);
      if (brouillon) {
        const p = brouillon.payload || {};
        if (p.formData) setFormData(p.formData);
        setStep(p.step || brouillon.step || 1);
        if (p.analyseType) setAnalyseType(p.analyseType);
        setBrouillonId(brouillon.id);
        if (p.puissancesFacturesParPDL) {
          setPuissancesFacturesParPDL(p.puissancesFacturesParPDL);
        }
        if (p.plagesHorairesCustom) {
          plagesHorairesCustomRef.current = p.plagesHorairesCustom;
        }
        if (p.preconisationsPuissanceParPlage) {
          preconisationsPuissanceParPlageRef.current = p.preconisationsPuissanceParPlage;
        }
        if (p.preconisationsPuissance) {
          preconisationsPuissanceRef.current = p.preconisationsPuissance;
        }
        if (p.consommationsParPlage) {
          consommationsParPlageRef.current = p.consommationsParPlage;
        }
        if (p.puissancesPreconiseesValidees) {
          setPuissancesPreconiseesValidees(p.puissancesPreconiseesValidees);
        }
        if (p.commentairesParPDL) {
          setCommentairesParPDL(p.commentairesParPDL);
        }
        cmdpsOverrideRef.current = p.cmdpsOverride || {};
        // Restauration des courbes de charge (forme légère échantillonnée) :
        // reconstruit puissancesMaxParPDL + métadonnées → les économies se recalculent
        // et le PDF se génère sans réimport manuel des fichiers Enedis.
        let courbesRestaurees = 0;
        if (p.graphDataParPDL && Object.keys(p.graphDataParPDL).length > 0) {
          const puissAcc = {};
          const metaAcc = {};
          Object.entries(p.graphDataParPDL).forEach(([pdlKey, gd]) => {
            if (Array.isArray(gd) && gd.length > 0) {
              const { pdlBlock, meta } = reconstruirePDLDepuisGraph(pdlKey, gd);
              const calc = p.calculsParPDL?.[pdlKey];
              pdlBlock.depassementsStockes = calc?.depassements || null;
              pdlBlock.puissancesActuellesStockees = calc?.puissancesActuelles || null;
              pdlBlock.puissancesProposeesStockees = calc?.puissancesProposees || null;
              puissAcc[pdlKey] = pdlBlock;
              metaAcc[pdlKey] = meta;
            }
          });
          if (Object.keys(puissAcc).length > 0) {
            setPuissancesMaxParPDL(puissAcc);
            setMetadonneesPDL(prev => ({ ...prev, ...metaAcc }));
            courbesRestaurees = Object.keys(puissAcc).length;
          }
        }
        if (p.calculsParPDL) setCalculsParPDL(p.calculsParPDL);
        setCalculsAJour(true);
        const nbPdlData = (p.pdlsAvecDonnees || []).length;
        if (courbesRestaurees > 0) {
          alert(`✓ Brouillon TURPE chargé.\n\n${courbesRestaurees} PDL restaurés avec leurs courbes de charge. Les économies sont recalculées et le PDF peut être généré directement.`);
        } else {
          alert(nbPdlData > 0
            ? `✓ Brouillon TURPE chargé.\n\n⚠️ Ce brouillon (ancienne version) ne contient pas les courbes de charge : réimportez vos fichiers Enedis à l'étape 4 (${nbPdlData} PDL) pour recalculer. Vos puissances, préconisations et commentaires ont été conservés.`
            : '✓ Brouillon TURPE chargé');
        }
      } else {
        alert('Brouillon introuvable');
      }
    } catch (error) {
      console.error('Erreur chargement brouillon:', error);
      alert('Erreur lors du chargement du brouillon.');
    }
  };

  const sauvegarderBrouillon = async () => {
    try {
      // Les courbes de charge sont sauvegardées sous forme LÉGÈRE (agrégées par heure
      // puis échantillonnées à 500 pts/PDL, comme les rapports). Cela reste sous la
      // limite MongoDB de 16 Mo même à ~200 PDL, tout en permettant de recalculer les
      // économies et de générer le PDF à la reprise, sans réimport manuel des fichiers.
      const graphDataParPDL = {};
      Object.entries(puissancesMaxParPDL).forEach(([pdlKey, data]) => {
        const pts = data?.tousLesPoints || [];
        if (pts.length > 0) {
          graphDataParPDL[pdlKey] = capGraphBrouillon(
            agregerParHeure(pts).map(pt => ({ horodate: pt.horodate, kW: pt.kW, kVA: pt.kVA }))
          );
        }
      });
      const saved = await apiSaveBrouillon({
        id: brouillonId || undefined,
        type: 'turpe',
        nom_entreprise: formData?.nom_entreprise || '',
        siren: formData?.siren || '',
        step,
        payload: {
          step,
          analyseType,
          formData,
          puissancesFacturesParPDL,
          plagesHorairesCustom: plagesHorairesCustomRef.current,
          preconisationsPuissanceParPlage: preconisationsPuissanceParPlageRef.current,
          preconisationsPuissance: preconisationsPuissanceRef.current,
          consommationsParPlage: consommationsParPlageRef.current,
          puissancesPreconiseesValidees,
          commentairesParPDL,
          cmdpsOverride: cmdpsOverrideRef.current,
          calculsParPDL,
          graphDataParPDL,
          pdlsAvecDonnees: Object.keys(puissancesMaxParPDL),
        },
      });
      setBrouillonId(saved.id);
      const nbPdlData = Object.keys(graphDataParPDL).length;
      alert(nbPdlData > 0
        ? `✓ Brouillon TURPE enregistré.\n\n${nbPdlData} PDL sauvegardés avec leurs courbes de charge (forme allégée). À la reprise, tout sera restauré : économies recalculées et PDF générable directement.`
        : '✓ Brouillon TURPE enregistré');
    } catch (error) {
      console.error('Erreur sauvegarde brouillon:', error);
      alert('❌ Erreur sauvegarde: ' + error.message);
    }
  };

  const handleImportDonneesBrutes = (pdlNumber, event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const csvContent = e.target.result;

        // Parser données
        const resultat = parserDonneesBrutesTURPE(csvContent);

        // ✅ Vérification de cohérence : le PDL détecté dans le fichier
        // doit correspondre à celui ciblé. Si différence, demande confirmation.
        const pdlDetecte = (resultat?.pdl || '').toString().trim();
        const pdlCible = (pdlNumber || '').toString().trim();
        if (pdlDetecte && pdlCible && pdlDetecte !== pdlCible) {
          const ok = window.confirm(
            `⚠️ Incohérence détectée\n\n` +
            `Le fichier "${file.name}" contient le PDL :\n  ${pdlDetecte}\n\n` +
            `Mais vous l'importez sur le PDL :\n  ${pdlCible}\n\n` +
            `Voulez-vous vraiment l'importer quand même sur ${pdlCible} ?\n` +
            `(annuler = ne rien importer)`
          );
          if (!ok) {
            event.target.value = '';
            return;
          }
        }

        // Stocker résultats pour ce PDL
        setPuissancesMaxParPDL({
          ...puissancesMaxParPDL,
          [pdlNumber]: {
            tousLesPoints: resultat.tousLesPoints || [],
            puissancesMaxParMois: resultat.puissancesMaxParMois || []
          }
        });
        
        // Stocker les métadonnées (hasReactiveData, pasTempsMinutes)
        setMetadonneesPDL(prev => ({
          ...prev,
          [pdlNumber]: {
            hasReactiveData: resultat.hasReactiveData || false,
            hasInductiveCapacitive: resultat.hasInductiveCapacitive || false,
            hasInductiveData: resultat.hasInductiveData || false,
            hasCapacitiveData: resultat.hasCapacitiveData || false,
            pasTempsMinutes: resultat.pasTempsMinutes || 10
          }
        }));
        
        // Marquer les calculs comme non à jour
        setCalculsAJour(false);
        
        console.log('Résultats parser:', resultat);
        console.log('Métadonnées:', { hasReactiveData: resultat.hasReactiveData, pasTempsMinutes: resultat.pasTempsMinutes });
        
        const nbMois = resultat.puissancesMaxParMois?.length || 0;
        const nbPoints = resultat.tousLesPoints?.length || 0;
        const pasInfo = resultat.pasTempsMinutes ? ` (pas: ${resultat.pasTempsMinutes}min)` : '';
        const reactifInfo = resultat.hasReactiveData ? '\nDonnées réactives: Oui' : '\nDonnées réactives: Non (kW uniquement)';
        
        alert(`✓ Données importées\n\n${nbMois} mois analysés\n${nbPoints.toLocaleString('fr-FR')} points de mesure${pasInfo}${reactifInfo}\nPuissances calculées`);
        
      } catch (error) {
        console.error('Erreur import:', error);
        
        const debugContext = {
          operation: 'Import données brutes TURPE',
          pdl: pdlNumber,
          fichier: file.name,
          taille: file.size
        };
        
        setErrorContext(debugContext);
        setErrorDebug(error);
        
        alert(`❌ Erreur : ${error.message}\n\nConsultez la popup pour détails`);
      }
    };
    
    reader.readAsText(file);
    event.target.value = '';
  };

  // Auto-création des PDL détectés à l'import mais absents de la configuration.
  // (Enedis envoie parfois plusieurs fichiers pour un grand nombre de PDL.)
  const ajouterPDLsManquants = useCallback((numeros) => {
    setFormData(prev => {
      const existants = new Set(prev.pdls.map(p => p.pdl));
      const nouveaux = numeros
        .filter(n => n && !existants.has(n))
        .map(n => ({ pdl: n, tension: 'HTA', formule_actuelle: '', contrat: '', puissances_actuelles: {}, commentaire: '' }));
      if (nouveaux.length === 0) return prev;
      return { ...prev, pdls: [...prev.pdls, ...nouveaux] };
    });
  }, []);

  // Import centralisé de PLUSIEURS fichiers (mono ou multi-PDL, mélange accepté).
  // Cumule avec les imports précédents (pas d'écrasement) et crée les PDL manquants.
  const handleImportMultipleFichiers = async (event) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;
    
    const resultats = {};
    const erreurs = [];
    const pdlsNonTrouves = [];
    const pdlsConfiguresAvant = formData.pdls.map(p => p.pdl);
    
    const enregistrer = (pdl, r, fileName) => {
      resultats[pdl] = {
        tousLesPoints: r.tousLesPoints || [],
        puissancesMaxParMois: r.puissancesMaxParMois || [],
        hasReactiveData: r.hasReactiveData || false,
        hasInductiveCapacitive: r.hasInductiveCapacitive || false,
        hasInductiveData: r.hasInductiveData || false,
        hasCapacitiveData: r.hasCapacitiveData || false,
        pasTempsMinutes: r.pasTempsMinutes || 10,
        fileName,
      };
    };
    
    for (const file of files) {
      try {
        // Gros fichier : parsing en streaming via Web Worker (évite de charger tout le texte).
        if (file.size > 60 * 1024 * 1024) {
          const rmap = await parseEnedisFileStreaming(file);
          if (Object.keys(rmap).length > 0) {
            for (const pdl in rmap) enregistrer(pdl, rmap[pdl], file.name);
          } else {
            pdlsNonTrouves.push(file.name);
          }
          continue;
        }
        const csvContent = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = (e) => resolve(e.target.result);
          reader.onerror = reject;
          reader.readAsText(file);
        });
        
        // Fichier mono-PDL OU multi-PDL : on détecte puis on parse en conséquence.
        const pdlsDansLeFichier = detecterPDLsMultiples(csvContent);
        if (pdlsDansLeFichier.length > 1) {
          const resultatsMulti = parserENEDISMultiPDL(csvContent);
          for (const pdl in resultatsMulti) {
            enregistrer(pdl, resultatsMulti[pdl], file.name);
          }
        } else {
          const resultat = parserDonneesBrutesTURPE(csvContent);
          const pdlDetecte = resultat.pdl || extractPDLFromCSV(csvContent) || extractPDLFromFilename(file.name);
          if (pdlDetecte) {
            enregistrer(pdlDetecte, resultat, file.name);
          } else {
            pdlsNonTrouves.push(file.name);
          }
        }
      } catch (error) {
        console.error(`Erreur fichier ${file.name}:`, error);
        erreurs.push({ fileName: file.name, message: error.message });
      }
    }
    
    const pdlsDetectes = Object.keys(resultats);
    
    if (pdlsDetectes.length > 0) {
      const donneesPDL = {};
      const metadonnees = {};
      for (const pdl of pdlsDetectes) {
        donneesPDL[pdl] = {
          tousLesPoints: resultats[pdl].tousLesPoints,
          puissancesMaxParMois: resultats[pdl].puissancesMaxParMois,
        };
        metadonnees[pdl] = {
          hasReactiveData: resultats[pdl].hasReactiveData,
          hasInductiveCapacitive: resultats[pdl].hasInductiveCapacitive,
          hasInductiveData: resultats[pdl].hasInductiveData,
          hasCapacitiveData: resultats[pdl].hasCapacitiveData,
          pasTempsMinutes: resultats[pdl].pasTempsMinutes,
        };
      }
      // Cumul (merge) — ne remplace pas les données déjà importées
      setPuissancesMaxParPDL(prev => ({ ...prev, ...donneesPDL }));
      setMetadonneesPDL(prev => ({ ...prev, ...metadonnees }));
      // Crée automatiquement les PDL détectés mais non encore configurés
      ajouterPDLsManquants(pdlsDetectes);
      setCalculsAJour(false);
    }
    
    const imported = pdlsDetectes.map(pdl => ({
      pdl,
      fileName: resultats[pdl].fileName,
      nbPoints: resultats[pdl].tousLesPoints.length,
      hasReactive: resultats[pdl].hasReactiveData,
      hasCapacitive: resultats[pdl].hasCapacitiveData,
      isNew: !pdlsConfiguresAvant.includes(pdl),
    }));
    
    setImportRecap({
      mode: 'multi-files',
      filesTotal: files.length,
      pdlsConfigured: pdlsDetectes.length,
      imported,
      createdCount: imported.filter(i => i.isNew).length,
      notDetected: pdlsNonTrouves.map(f => ({ fileName: f })),
      notConfigured: [],
      missing: [],
      errors: erreurs.filter(e => typeof e === 'object'),
    });
    
    event.target.value = '';
  };

  // Import d'un fichier unique multi-PDL Enedis
  const [importMultiPDLProgress, setImportMultiPDLProgress] = useState(null);
  
  const handleImportFichierMultiPDL = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    // Gros fichiers : parsing en STREAMING via Web Worker (ne bloque pas l'onglet,
    // pas de limite du string en mémoire → gère les fichiers > 1 Go).
    const SEUIL_STREAMING = 60 * 1024 * 1024; // 60 Mo
    if (file.size > SEUIL_STREAMING) {
      const pdlsConfigures = formData.pdls.map(p => p.pdl);
      setImportMultiPDLProgress(1);
      try {
        const resultatsMulti = await parseEnedisFileStreaming(file, (pct) => setImportMultiPDLProgress(pct));
        const pdlsDetectes = Object.keys(resultatsMulti);
        if (pdlsDetectes.length === 0) {
          throw new Error("Aucun PDL détecté (format ENEDIS attendu : PDL en 1ère colonne, ≥ 9 colonnes).");
        }
        const donneesPDL = {};
        const metadonnees = {};
        const importedList = [];
        for (const pdl of pdlsDetectes) {
          const r = resultatsMulti[pdl];
          donneesPDL[pdl] = { tousLesPoints: r.tousLesPoints, puissancesMaxParMois: r.puissancesMaxParMois };
          metadonnees[pdl] = {
            hasReactiveData: r.hasReactiveData,
            hasInductiveCapacitive: r.hasInductiveCapacitive,
            hasInductiveData: r.hasInductiveData,
            hasCapacitiveData: r.hasCapacitiveData,
            pasTempsMinutes: r.pasTempsMinutes,
          };
          importedList.push({
            pdl,
            fileName: file.name,
            nbPoints: r.tousLesPoints.length,
            hasReactive: r.hasReactiveData,
            hasCapacitive: r.hasCapacitiveData,
            isNew: !pdlsConfigures.includes(pdl),
          });
        }
        setPuissancesMaxParPDL(prev => ({ ...prev, ...donneesPDL }));
        setMetadonneesPDL(prev => ({ ...prev, ...metadonnees }));
        ajouterPDLsManquants(pdlsDetectes);
        setCalculsAJour(false);
        setImportMultiPDLProgress(100);
        setImportRecap({
          mode: 'multi-pdl-file',
          filesTotal: 1,
          pdlsConfigured: pdlsDetectes.length,
          pdlsInFile: pdlsDetectes.length,
          imported: importedList,
          createdCount: importedList.filter(i => i.isNew).length,
          notDetected: [],
          notConfigured: [],
          missing: [],
          errors: [],
        });
      } catch (err) {
        console.error('Erreur import multi-PDL (streaming):', err);
        setImportRecap({
          mode: 'multi-pdl-file',
          filesTotal: 1,
          pdlsConfigured: formData.pdls.length,
          imported: [],
          notDetected: [],
          notConfigured: [],
          missing: [],
          errors: [{ fileName: file.name, message: err.message }],
        });
      } finally {
        setTimeout(() => setImportMultiPDLProgress(null), 500);
        event.target.value = '';
      }
      return;
    }
    
    setImportMultiPDLProgress(0);
    
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const csvContent = e.target.result;
        const pdlsConfigures = formData.pdls.map(p => p.pdl);
        
        // Détecter les PDL dans le fichier
        const pdlsDansLeFichier = detecterPDLsMultiples(csvContent);
        
        if (pdlsDansLeFichier.length <= 1) {
          // Fichier mono-PDL : utiliser le parser standard
          const resultat = parserDonneesBrutesTURPE(csvContent);
          const pdlDetecte = resultat.pdl || extractPDLFromCSV(csvContent) || extractPDLFromFilename(file.name);
          
          if (pdlDetecte) {
            setPuissancesMaxParPDL(prev => ({
              ...prev,
              [pdlDetecte]: {
                tousLesPoints: resultat.tousLesPoints || [],
                puissancesMaxParMois: resultat.puissancesMaxParMois || []
              }
            }));
            setMetadonneesPDL(prev => ({
              ...prev,
              [pdlDetecte]: { hasReactiveData: resultat.hasReactiveData, hasInductiveCapacitive: resultat.hasInductiveCapacitive, hasInductiveData: resultat.hasInductiveData, hasCapacitiveData: resultat.hasCapacitiveData, pasTempsMinutes: resultat.pasTempsMinutes }
            }));
            ajouterPDLsManquants([pdlDetecte]);
            setCalculsAJour(false);
            setImportRecap({
              mode: 'multi-pdl-file',
              filesTotal: 1,
              pdlsConfigured: 1,
              imported: [{
                pdl: pdlDetecte,
                fileName: file.name,
                nbPoints: resultat.tousLesPoints?.length || 0,
                hasReactive: resultat.hasReactiveData,
                hasCapacitive: resultat.hasCapacitiveData,
                isNew: !pdlsConfigures.includes(pdlDetecte),
              }],
              notDetected: [],
              notConfigured: [],
              missing: [],
              errors: [],
            });
          } else {
            setImportRecap({
              mode: 'multi-pdl-file',
              filesTotal: 1,
              pdlsConfigured: pdlsConfigures.length,
              imported: [],
              notDetected: [{ fileName: file.name }],
              notConfigured: [],
              missing: pdlsConfigures.map(p => ({ pdl: p })),
              errors: [],
            });
          }
          setImportMultiPDLProgress(null);
          event.target.value = '';
          return;
        }
        
        // Fichier multi-PDL : parser avec le multi-PDL parser
        setImportMultiPDLProgress(5);
        
        const resultatsMulti = parserENEDISMultiPDL(csvContent, (pct) => {
          setImportMultiPDLProgress(pct);
        });
        
        setImportMultiPDLProgress(95);
        
        // Distribuer les résultats aux PDL configurés
        const donneesPDL = {};
        const metadonnees = {};
        const importedList = [];
        const notConfiguredList = [];
        const missingList = [];
        
        for (const pdl in resultatsMulti) {
          donneesPDL[pdl] = {
            tousLesPoints: resultatsMulti[pdl].tousLesPoints,
            puissancesMaxParMois: resultatsMulti[pdl].puissancesMaxParMois
          };
          metadonnees[pdl] = {
            hasReactiveData: resultatsMulti[pdl].hasReactiveData,
            hasInductiveCapacitive: resultatsMulti[pdl].hasInductiveCapacitive,
            hasInductiveData: resultatsMulti[pdl].hasInductiveData,
            hasCapacitiveData: resultatsMulti[pdl].hasCapacitiveData,
            pasTempsMinutes: resultatsMulti[pdl].pasTempsMinutes
          };
          importedList.push({
            pdl,
            fileName: file.name,
            nbPoints: resultatsMulti[pdl].tousLesPoints.length,
            hasReactive: resultatsMulti[pdl].hasReactiveData,
            hasCapacitive: resultatsMulti[pdl].hasCapacitiveData,
            isNew: !pdlsConfigures.includes(pdl),
          });
        }
        
        if (Object.keys(donneesPDL).length > 0) {
          setPuissancesMaxParPDL(prev => ({ ...prev, ...donneesPDL }));
          setMetadonneesPDL(prev => ({ ...prev, ...metadonnees }));
          ajouterPDLsManquants(Object.keys(donneesPDL));
          setCalculsAJour(false);
        }
        
        setImportRecap({
          mode: 'multi-pdl-file',
          filesTotal: 1,
          pdlsConfigured: Object.keys(resultatsMulti).length,
          pdlsInFile: Object.keys(resultatsMulti).length,
          imported: importedList,
          createdCount: importedList.filter(i => i.isNew).length,
          notDetected: [],
          notConfigured: notConfiguredList,
          missing: missingList,
          errors: [],
        });
        setImportMultiPDLProgress(null);
        
      } catch (error) {
        console.error('Erreur import multi-PDL:', error);
        setImportRecap({
          mode: 'multi-pdl-file',
          filesTotal: 1,
          pdlsConfigured: formData.pdls.length,
          imported: [],
          notDetected: [],
          notConfigured: [],
          missing: formData.pdls.map(p => ({ pdl: p.pdl })),
          errors: [{ fileName: file.name, message: error.message }],
        });
        setImportMultiPDLProgress(null);
      }
    };
    
    reader.readAsText(file);
    event.target.value = '';
  };

  // Agréger par heure (max) pour courbe de charge
  const agregerParHeure = (tousLesPoints) => {
    const parHeure = {};
    
    tousLesPoints.forEach(point => {
      // Tronquer à l'heure : "2024-04-22 14:00:00"
      const heure = point.horodate.substring(0, 13) + ':00:00';
      
      if (!parHeure[heure]) {
        parHeure[heure] = { kW: 0, kVA: 0 };
      }
      
      parHeure[heure].kW = Math.max(parHeure[heure].kW, point.kW);
      parHeure[heure].kVA = Math.max(parHeure[heure].kVA, point.kVA);
    });
    
    // Convertir en tableau et trier chronologiquement
    return Object.entries(parHeure)
      .map(([horodate, vals]) => ({
        horodate: horodate,
        kW: vals.kW,
        kVA: vals.kVA
      }))
      .sort((a, b) => a.horodate.localeCompare(b.horodate));
  };

  const downloadTemplateMultiPDL = () => {
    if (formData.pdls.length === 0) {
      alert('Ajoutez d\'abord des PDL à l\'étape 3');
      return;
    }

    const wb = XLSX.utils.book_new();
    
    // En-têtes : PDL | puis une colonne par plage pour puissances ET consommations
    const plagesUniques = new Set();
    formData.pdls.forEach(pdl => {
      const plages = PLAGES_PAR_FORMULE[pdl.formule_actuelle] || [];
      plages.forEach(p => plagesUniques.add(p));
    });
    
    const plagesArray = Array.from(plagesUniques);
    
    const headers = ['PDL'];
    plagesArray.forEach(plage => {
      headers.push(`${plage} (kVA)`);
      headers.push(`${plage} (kWh)`);
    });
    
    const data = [headers];
    
    // Une ligne par PDL
    formData.pdls.forEach(pdl => {
      const row = [pdl.pdl];
      const plagesPDL = PLAGES_PAR_FORMULE[pdl.formule_actuelle] || [];
      
      plagesArray.forEach(plage => {
        if (plagesPDL.includes(plage)) {
          row.push(0); // kVA
          row.push(0); // kWh
        } else {
          row.push(''); // Non applicable
          row.push('');
        }
      });
      
      data.push(row);
    });
    
    const ws = XLSX.utils.aoa_to_sheet(data);
    XLSX.utils.book_append_sheet(wb, ws, 'Données TURPE');
    XLSX.writeFile(wb, `template_donnees_turpe_${formData.nom_client || 'analyse'}.xlsx`);
    
    alert('✓ Template téléchargé\n\nRemplissez puissances (kVA) et consommations (kWh) par plage');
  };

  // === Puissances souscrites : modèle à télécharger (une ligne par PDL) ===
  const PLAGES_PUISSANCES = ['HPH', 'HCH', 'HPB', 'HCB', 'Pointe', 'Unique'];

  const downloadTemplatePuissances = () => {
    const headers = ['PDL', ...PLAGES_PUISSANCES];
    const data = [headers];
    const pdls = formData.pdls.length ? formData.pdls : [{ pdl: '', puissances_actuelles: {} }];
    pdls.forEach(p => {
      const pa = p.puissances_actuelles || {};
      data.push([p.pdl, ...PLAGES_PUISSANCES.map(pl => (pa[pl] ?? ''))]);
    });
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet(data);
    XLSX.utils.book_append_sheet(wb, ws, 'Puissances souscrites');
    XLSX.writeFile(wb, `template_puissances_souscrites_${formData.nom_client || 'analyse'}.xlsx`);
    alert('✓ Modèle téléchargé\n\nRenseignez la puissance souscrite (kVA) de chaque PDL par plage horaire, puis réimportez le fichier.');
  };

  // === Import du fichier des puissances souscrites (xlsx/csv) ===
  const handleImportPuissancesSouscrites = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const wb = XLSX.read(new Uint8Array(e.target.result), { type: 'array' });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json(ws, { defval: '' });
        const PLAGES_VALIDES = ['Pointe', 'HPH', 'HCH', 'HPB', 'HCB', 'HP', 'HC', 'Unique', 'Base', 'max'];
        const parPDL = {};
        let nbPDL = 0;
        rows.forEach(row => {
          const keys = Object.keys(row);
          const pdlKey = keys.find(k => k.trim().toUpperCase() === 'PDL');
          const pdlNum = pdlKey ? String(row[pdlKey]).trim() : '';
          if (!pdlNum) return;
          const pa = {};
          keys.forEach(k => {
            const canon = PLAGES_VALIDES.find(pl => pl.toUpperCase() === k.trim().toUpperCase());
            if (canon) {
              const val = parseFloat(String(row[k]).replace(',', '.'));
              if (!isNaN(val) && val > 0) pa[canon] = val;
            }
          });
          if (Object.keys(pa).length > 0) {
            parPDL[pdlNum] = pa;
            nbPDL++;
          }
        });
        if (nbPDL === 0) {
          alert('Aucune puissance souscrite valide trouvée.\n\nVérifiez la colonne « PDL » et au moins une colonne de plage (HPH, HCH, HPB, HCB, Pointe, Unique).');
          event.target.value = '';
          return;
        }
        // Créer les PDL absents puis fusionner les puissances souscrites
        ajouterPDLsManquants(Object.keys(parPDL));
        setFormData(prev => ({
          ...prev,
          pdls: prev.pdls.map(p => parPDL[p.pdl]
            ? {
                ...p,
                tension: parPDL[p.pdl].Unique !== undefined ? 'BT' : p.tension,
                puissances_actuelles: { ...p.puissances_actuelles, ...parPDL[p.pdl] },
              }
            : p),
        }));
        setCalculsAJour(false);
        alert(`✓ Puissances souscrites importées pour ${nbPDL} PDL`);
      } catch (error) {
        console.error('Erreur import puissances souscrites:', error);
        alert(`❌ Erreur d'import : ${error.message}`);
      }
      event.target.value = '';
    };
    reader.readAsArrayBuffer(file);
  };

  const handleTypeSelection = (type) => {
    setAnalyseType(type);
    setStep(2);
  };

  // === Contexte transmis aux sous-composants Step4/5/6/7 ===
  // Un seul prop pour limiter le drilling — chaque step déstructure ce dont il a besoin.
  const stepCtx = useMemo(() => ({
    // States
    step, analyseType, formData, agences, commerciaux, currentPDL,
    puissancesMaxParPDL, analyseResults, preconisations, zoomReset,
    visibiliteCourbes, commentairesParPDL, puissancesFacturesParPDL,
    plagesHorairesModalPDL, calculsRapport, calculsParPDL, metadonneesPDL,
    calculsAJour, puissancesPreconiseesValidees, importMultiPDLProgress,
    importRecap,
    editMode, editRapportId,
    // Setters
    setStep, setAnalyseType, setFormData, setCurrentPDL,
    setPuissancesMaxParPDL, setAnalyseResults, setPreconisations, setZoomReset,
    setVisibiliteCourbes, setCommentairesParPDL, setPuissancesFacturesParPDL,
    setPlagesHorairesModalPDL, setCalculsRapport, setCalculsParPDL,
    setMetadonneesPDL, setCalculsAJour, setPuissancesPreconiseesValidees,
    setImportRecap,
    // Refs
    preconisationsPuissanceRef, preconisationsPuissanceParPlageRef,
    consommationsParPlageRef, cerOverrideRef, cmdpsOverrideRef, plagesHorairesCustomRef,
    // Handlers / fonctions utilitaires
    sauvegarderBrouillon, actualiserCalculs,
    handleSaveCommentaire, handleSavePuissanceParPlage, handleSavePuissanceCourbe,
    handleSaveConsommationParPlage, handleSavePuissanceFacture,
    handleImportDonneesBrutes, handleImportFichierMultiPDL, handleImportMultipleFichiers,
    downloadTemplatePuissances, handleImportPuissancesSouscrites,
    determinerPlageHoraire, calculerConsommationsParPlage, agregerParHeure,
    // Composants memoizés locaux
    PuissanceInput, CommentaireInput,
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [
    step, analyseType, formData, agences, commerciaux, currentPDL,
    puissancesMaxParPDL, analyseResults, preconisations, zoomReset,
    visibiliteCourbes, commentairesParPDL, puissancesFacturesParPDL,
    plagesHorairesModalPDL, calculsRapport, calculsParPDL, metadonneesPDL,
    calculsAJour, puissancesPreconiseesValidees, importMultiPDLProgress,
    importRecap,
    editMode, editRapportId,
  ]);

  return (
    <div className="max-w-6xl mx-auto">
      {/* Modal de personnalisation des plages horaires */}
      {plagesHorairesModalPDL && (
        <PlagesHorairesConfig
          pdl={plagesHorairesModalPDL}
          formule={formData.pdls.find(p => p.pdl === plagesHorairesModalPDL)?.formule_actuelle || ''}
          currentConfig={plagesHorairesCustomRef.current[plagesHorairesModalPDL]}
          onSave={(config) => {
            plagesHorairesCustomRef.current = {
              ...plagesHorairesCustomRef.current,
              [plagesHorairesModalPDL]: config
            };
            // Invalider le cache des consommations pour ce PDL (recalcul avec nouvelles plages)
            const newConso = { ...consommationsParPlageRef.current };
            delete newConso[plagesHorairesModalPDL];
            consommationsParPlageRef.current = newConso;
            setPlagesHorairesModalPDL(null);
            setCalculsAJour(false);
          }}
          onClose={() => setPlagesHorairesModalPDL(null)}
        />
      )}
      <h1 className="text-4xl font-heading font-semibold text-[#191D1E] mb-2 tracking-tight">
        {editMode ? 'Modifier l\'Analyse TURPE' : 'Créer une Analyse TURPE'}
      </h1>
      <p className="text-[#687175] mb-8">
        {editMode
          ? `Modification du rapport — ${formData.nom_client || ''} (${formData.siren || ''})`
          : 'Optimisation du Tarif d\'Utilisation des Réseaux Publics d\'Électricité'}
      </p>

      {/* Indicateur Mode Édition */}
      {editMode && (
        <div className="mb-6 p-4 bg-[#DBEAFE] border border-[#397DFF] rounded-lg flex items-center gap-3">
          <Pencil className="w-5 h-5 text-[#397DFF]" />
          <div>
            <div className="font-medium text-[#191D1E]">Mode modification</div>
            <div className="text-sm text-[#687175]">Les données du rapport sont pré-chargées. Modifiez les puissances ou la configuration puis régénérez.</div>
          </div>
        </div>
      )}
      
      {loadingEdit && (
        <div className="mb-6 p-8 text-center">
          <div className="animate-spin w-8 h-8 border-4 border-[#397DFF] border-t-transparent rounded-full mx-auto mb-4"></div>
          <div className="text-[#687175]">Chargement du rapport TURPE...</div>
        </div>
      )}

      {/* Progress bar - Cliquable pour navigation */}
      <div className="mb-8 flex items-center gap-1">
        {[
          { num: 1, label: 'Type' },
          { num: 2, label: 'Infos' },
          { num: 3, label: 'PDL' },
          { num: 4, label: 'Import' },
          { num: 5, label: 'Analyse' },
          { num: 6, label: 'Validation' },
          { num: 7, label: 'Rapport', isLast: true }
        ].map((s, idx) => (
          <React.Fragment key={s.num}>
            {idx > 0 && <div className="h-px flex-1 bg-[#BFC6CC]"></div>}
            <button
              onClick={() => setStep(s.num)}
              disabled={!editMode && s.num > step}
              className={`flex items-center gap-1 ${(editMode || s.num <= step) ? 'cursor-pointer' : 'cursor-default opacity-50'} ${step >= s.num ? (s.isLast ? 'text-[#10D183]' : 'text-[#397DFF]') : 'text-[#687175]'}`}
            >
              <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs ${step >= s.num ? (s.isLast ? 'bg-[#10D183] text-white' : 'bg-[#397DFF] text-white') : 'bg-[#BFC6CC] text-white'} ${s.num < step ? 'hover:ring-2 hover:ring-[#397DFF]/30' : ''}`}>{s.num}</div>
              <span className="text-xs font-medium hidden lg:inline">{s.label}</span>
            </button>
          </React.Fragment>
        ))}
      </div>

      {/* Étape 1 : Type analyse */}
      {step === 1 && (
        <StepTypeSelection onSelect={handleTypeSelection} />
      )}

      {/* Étape 2 : Infos client */}
      {step === 2 && (
        <StepClientInfo 
          formData={formData}
          setFormData={setFormData}
          agences={agences}
          commerciaux={commerciaux}
          onPrev={() => setStep(1)}
          onNext={() => setStep(3)}
          onSaveDraft={sauvegarderBrouillon}
        />
      )}

      {/* Étape 3 : Configuration PDL */}
      {step === 3 && (
        <StepPDLConfig
          analyseType={analyseType}
          formData={formData}
          setFormData={setFormData}
          onPrev={() => setStep(2)}
          onNext={() => setStep(4)}
          onSaveDraft={sauvegarderBrouillon}
        />
      )}

      {/* Étape 4 : Données et Puissances */}
      {step === 4 && <Step4Donnees ctx={stepCtx} />}

      {/* Étape 5 : Analyse et Préconisation */}
      {step === 5 && <Step5Analyse ctx={stepCtx} />}

      {/* Étape 6 : Validation calculs avant/après */}
      {step === 6 && <Step6Validation ctx={stepCtx} />}

      {/* Étape 7 : Génération Rapport */}
      {step === 7 && <Step7Generation ctx={stepCtx} />}
      
      {/* Modal Debug Erreur */}
      <ErrorDebugModal 
        error={errorDebug}
        context={errorContext}
        onClose={() => {
          setErrorDebug(null);
          setErrorContext(null);
        }}
      />
      
      {/* Bouton flottant d'actualisation des calculs */}
      {!calculsAJour && step === 5 && (
        <div className="fixed bottom-24 right-6 z-50">
          <button
            onClick={actualiserCalculs}
            className="flex items-center gap-3 px-6 py-3 bg-yellow-500 hover:bg-yellow-600 text-white rounded-full shadow-xl font-medium transition-all transform hover:scale-105 animate-pulse"
            data-testid="floating-refresh-btn"
          >
            <RefreshCcw className="w-5 h-5" />
            <span>Actualiser les calculs</span>
          </button>
        </div>
      )}
      
      {/* Boutons de navigation flottants (haut/bas) */}
      <div className="fixed bottom-6 right-6 flex flex-col gap-2 z-50">
        <button
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          className="w-10 h-10 bg-white border border-[#BFC6CC] rounded-full shadow-lg flex items-center justify-center hover:bg-[#F5F6F9] transition-colors"
          title="Remonter en haut"
        >
          <svg className="w-5 h-5 text-[#687175]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
          </svg>
        </button>
        <button
          onClick={() => window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' })}
          className="w-10 h-10 bg-white border border-[#BFC6CC] rounded-full shadow-lg flex items-center justify-center hover:bg-[#F5F6F9] transition-colors"
          title="Descendre en bas"
        >
          <svg className="w-5 h-5 text-[#687175]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
      </div>
    </div>
  );
};

export default CreateAnalyseTURPE;
